import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Card, Table, Pagination, Button, Badge } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import { useTranslation } from "react-i18next";
import RoundedPagination from "../../Components/Paginate";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { logout } from "../../redux/actions/AuthActions";
import useDebounce from "../../hooks/useDebounce";
import { getDisplayDateTimeFormat } from "../../util/generalUtills";
import Preloader from "../../Components/Preloader";
import AddCaseInitiated from "../../Components/FormComponent/AddCaseInitiated";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faFilter, faTimes } from "@fortawesome/free-solid-svg-icons";
import FilterModal from "../../Components/FilterModal";
import { getCaseDetailsList, getMotorCaseDetailsList } from "../../redux/actions/CaseDetailsActions";
import { getMasterClaimTypeList } from "../../redux/actions/MasterActions";
import { CLAIM_TYPE_CATEGORY } from "../../constants/claimTypeCategory";
import {
  appendMotorRouteSuffix,
  isMotorCaseRoute,
  pathsMatchCaseRoute,
  resolveCaseApiEndpoint,
  resolveCaseClaimCategory,
  resolveActiveCaseMenuData,
  resolveActiveCasePermissions,
  resolveActiveCaseStatusCount,
  hasCasePermissions,
  getCaseSummaryPath,
  stripMotorRouteSuffix,
} from "../../util/caseCategoryNavigation";

const CaseDetailsIndex = ({ case_url, title }) => {
  const { t } = useTranslation();
  const [swalProps, setSwalProps] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editCaseDetailsId, setEditCaseDetailsId] = useState(null);

  const [showCaseInitiatedModel, setShowCaseInitiatedModel] = useState(false);
  const hideCaseInitiatedModel = () => {
    setShowCaseInitiatedModel(false);
    refreshCaseList();
  };

  const {
    AuthReducer: { auth, errors, loading },
    caseDetailsReducer: {
      caseDetailsPermissions,
      caseDetailsMenudata,
      caseDetailsLists,
      loadingCaseDetails,
      errorsCaseDetails,
      caseDetailsCount,
      changedCaseDetailsStatus,
      caseStatusCount,
      motorCaseDetailsPermissions,
      motorCaseDetailsMenudata,
      motorCaseDetailsLists,
      loadingMotorCaseDetails,
      motorCaseDetailsCount,
      motorCaseStatusCount,
    },
    masterReducer: { masterClaimTypeList = [] },
  } = useSelector((state) => state);
  const [page, setPage] = useState(1);
  const [paginate, setPaginate] = useState(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState('desc');
  const [filters, setFilters] = useState({});
  const [modal, setModal] = useState({ show: false, key: "", type: "", value: "" });
  const indexOfLastItem = (page + 1) * paginate;
  const indexOfFirstItem = indexOfLastItem - paginate;

  // Generate API endpoint based on current route
  const getCurrentApiEndpoint = useMemo(
    () => resolveCaseApiEndpoint(location.pathname, case_url),
    [location.pathname, case_url]
  );

  const isInitiatedListEndpoint = useMemo(() => {
    const baseEndpoint = String(getCurrentApiEndpoint).replace(/-Motor$/, "");
    return baseEndpoint === "Initiated" || baseEndpoint === "My-Case-Initiated";
  }, [getCurrentApiEndpoint]);

  const caseClaimCategory = useMemo(
    () => resolveCaseClaimCategory(location.pathname),
    [location.pathname]
  );

  const isMotorCase = caseClaimCategory === CLAIM_TYPE_CATEGORY.MOTOR;
  const activeCaseDetailsLists = isMotorCase ? motorCaseDetailsLists : caseDetailsLists;
  const activeLoadingCaseDetails = isMotorCase ? loadingMotorCaseDetails : loadingCaseDetails;
  const activeCaseDetailsPermissions = resolveActiveCasePermissions(
    isMotorCase,
    motorCaseDetailsPermissions,
    caseDetailsPermissions
  );
  const activeCaseDetailsMenudata = resolveActiveCaseMenuData(
    isMotorCase,
    motorCaseDetailsMenudata,
    caseDetailsMenudata
  );
  const activeCaseDetailsCount = isMotorCase ? motorCaseDetailsCount : caseDetailsCount;
  const activeCaseStatusCount = resolveActiveCaseStatusCount(
    isMotorCase,
    motorCaseStatusCount,
    caseStatusCount
  );

  const refreshCaseList = useCallback(() => {
    if (!auth?.token || !getCurrentApiEndpoint) return;
    const listArgs = [getCurrentApiEndpoint, auth.token, paginate, page, search, sort, filters];
    if (isMotorCase) {
      dispatch(getMotorCaseDetailsList(...listArgs));
    } else {
      dispatch(getCaseDetailsList(...listArgs));
    }
  }, [
    auth?.token,
    dispatch,
    filters,
    getCurrentApiEndpoint,
    isMotorCase,
    page,
    paginate,
    search,
    sort,
  ]);

  const hideModel = () => {
    setShowModal(false);
    setEditCaseDetailsId(null);
    refreshCaseList();
  };

  useEffect(() => {
    if (errors) {
      if (errors?.status == 401) {
        dispatch(logout())
        toast.error(errors?.data?.message);
        navigate('/login');
        window.location.reload();
      }
    }
  }, [errors])

  // Single useEffect for API calls - only when route or filters change
  useEffect(() => {
    refreshCaseList();
  }, [refreshCaseList, location.pathname]);

  // Motor list may not return permissions/menu yet — load health metadata as fallback
  useEffect(() => {
    if (!auth?.token || !isMotorCase) return;
    if (hasCasePermissions(motorCaseDetailsPermissions)) return;
    if (hasCasePermissions(caseDetailsPermissions)) return;

    const baseEndpoint = String(getCurrentApiEndpoint).replace(/-Motor$/, "");
    dispatch(getCaseDetailsList(baseEndpoint, auth.token, 10, 1, "", sort, {}));
  }, [
    auth?.token,
    isMotorCase,
    getCurrentApiEndpoint,
    motorCaseDetailsPermissions,
    caseDetailsPermissions,
    dispatch,
    sort,
  ]);

  const handlePaginateChange = (e) => {
    const value = e.target.value === "All" ? activeCaseDetailsCount : parseInt(e.target.value);
    setPaginate(value);
    setPage(1); // Reset to the first page whenever the paginate value changes
  };

  const handleFilterClick = (key) => {
    if (!filters[key]) {
      setFilters((prev) => ({
        ...prev,
        [key]: { type: "", value: "" },
      }));
    }
    setModal({ show: true, key, type: filters[key]?.type || "", value: filters[key]?.value || "" });
  };

  const handleModalChange = (field, value) => {
    setModal((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilter = () => {
    setFilters((prev) => {
      const next = { ...prev };
      if (modal.key === "claim_type" && !modal.value) {
        delete next.claim_type;
      } else {
        next[modal.key] = { type: "Contains", value: modal.value };
      }
      return next;
    });
    setModal({ show: false, key: "", type: "", value: "" });
  };

  const handleRemoveFilter = (key) => {
    setFilters((prev) => {
      const updatedFilters = { ...prev };
      delete updatedFilters[key];
      return updatedFilters;
    });
    setModal({ show: false, key: "", type: "", value: "" });
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: errors?.data?.state_code ? 'red' : base.borderColor,
      '&:hover': {
        borderColor: errors?.data?.state_code ? 'red' : base.borderColor,
      }
    }),
  };

  useEffect(() => {
    document.title = `${title} :: ${auth?.generalSettings?.title}`;
  }, []);

  useEffect(() => {
    if (auth?.token) {
      dispatch(getMasterClaimTypeList(auth.token, caseClaimCategory));
    }
  }, [auth?.token, dispatch, caseClaimCategory]);

  const normalize = useCallback((str) => {
    if (!str) return "";
    return String(str)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }, []);

  const statusSynonyms = useMemo(() => ({
    "assign field officer": "assigned field officer",
    "accepted by fo": "case accept by fo",
    "rejected by fo": "case reject by fo",
  }), []);

  const statusCountMap = useMemo(() => {
    const map = {};
    (activeCaseStatusCount || []).forEach((item) => {
      map[normalize(item?.case_status)] = Number(item?.total) || 0;
    });
    return map;
  }, [activeCaseStatusCount, normalize]);

  // Calculate time remaining until case_close_date
  const calculateTimeRemaining = (caseCloseDate, claimTypeName) => {
    if (!caseCloseDate) return 'N/A';
    
    try {
      const closeDate = new Date(caseCloseDate);
      closeDate.setHours(23, 59, 59, 999);
      
      const now = new Date();
      const diffMs = closeDate.getTime() - now.getTime();
      
      if (diffMs <= 0) {
        return 'Expired';
      }
      
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      const remainingHours = diffHours % 24;
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (claimTypeName && claimTypeName.toLowerCase() === 'cashless') {
        if (diffHours > 0) {
          return `${diffHours} hour${diffHours > 1 ? 's' : ''} ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} left`;
        } else {
          return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} left`;
        }
      }
      
      if (diffDays > 0) {
        if (remainingHours > 0) {
          return `${diffDays} day${diffDays > 1 ? 's' : ''} ${remainingHours} hour${remainingHours > 1 ? 's' : ''} left`;
        } else {
          return `${diffDays} day${diffDays > 1 ? 's' : ''} left`;
        }
      } else {
        return `${remainingHours} hour${remainingHours > 1 ? 's' : ''} left`;
      }
    } catch (error) {
      console.error('Error calculating time remaining:', error);
      return 'N/A';
    }
  };

  useEffect(() => {
    setActiveTab(location.pathname);
  }, [location.pathname])

  // Find the correct menu data based on current route
  const getCurrentMenuData = useMemo(() => {
    // Check if caseDetailsMenudata is an array and has data
    if (!activeCaseDetailsMenudata || !Array.isArray(activeCaseDetailsMenudata) || activeCaseDetailsMenudata.length === 0) {
      return null;
    }
    
    const basePath = stripMotorRouteSuffix(location.pathname);
    const isMyCaseRoute = basePath.startsWith('/My-Case');
    
    // Find menu that matches the current route type
    const matchingMenu = activeCaseDetailsMenudata.find(menu => {
      if (!menu || !menu.children || !Array.isArray(menu.children) || menu.children.length === 0) {
        return false;
      }
      
      if (isMyCaseRoute) {
        // For My Cases, look for menu whose children have URLs starting with /My-Case
        return menu.children.some(child => child?.url?.startsWith("/My-Case"));
      } else {
        // For Active Case, look for menu whose children have URLs NOT starting with /My-Case
        return menu.children.some(child => child?.url && !child.url.startsWith("/My-Case"));
      }
    });
    
    // Fallback to first menu if no match found
    return matchingMenu || (activeCaseDetailsMenudata.length > 0 ? activeCaseDetailsMenudata[0] : null);
  }, [activeCaseDetailsMenudata, location.pathname]);

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: title, path: case_url, active: true },
        ]}
        title={title}
      />

      <div style={{
        overflowX: "auto",
        whiteSpace: "nowrap",
        padding: "10px",
        marginBottom: "5px",
        marginTop: "5px"
      }}>
        <Row style={{
          display: "inline-flex",
          flexWrap: "nowrap",
          gap: "0px",
          padding: "10 0px"
        }}>
          {getCurrentMenuData?.children?.length > 0 &&
            getCurrentMenuData.children.map((child) => {
              // Check if current location matches child URL or if it's the active tab
              const isActive =
                pathsMatchCaseRoute(location.pathname, child.url) ||
                pathsMatchCaseRoute(activeTab, child.url);
              const labelLength = child.label.length;
              const dynamicWidth = Math.max(120, labelLength * 8 + 40);


              return (
                <Col key={child.id} style={{
                  minWidth: `${dynamicWidth}px`,
                  width: `${dynamicWidth}px`,
                  flex: "0 0 auto"
                }}>
                  <Card
                    onClick={() => {
                      if (child.url) {
                        const basePath = stripMotorRouteSuffix(location.pathname);
                        const isMyCaseRoute = basePath.startsWith('/My-Case');
                        const keepMotorSuffix = isMotorCaseRoute(location.pathname);

                        let targetUrl = child.url;

                        if (isMyCaseRoute) {
                          if (child.url.startsWith('/My-Case')) {
                            targetUrl = child.url;
                          } else if (child.url.startsWith('/')) {
                            const routeName = child.url.substring(1);

                            const routeMapping = {
                              'Initiated': '/My-Case-Initiated',
                              'On-Progress': '/My-Case-On-Progress',
                              'Assign-Field-Officer': '/My-Case-Assign-Field-Officer',
                              'Accepted-By-FO': '/My-Case-Accepted-By-FO',
                              'Rejected-By-FO': '/My-Case-Rejected-By-FO',
                              'Field-QC-Completed': '/My-Case-Field-QC-Pending',
                              'Waiting-for-Doctor-Allocation': '/My-Case-Waiting-for-Doctor-Allocation',
                              'Doctor-Report-Pending': '/My-Case-Doctor-Report-Pending',
                              'Manager-QC-Pending': '/My-Case-Manager-QC-Pending',
                              'Team-Leader-QC-Pending': '/My-Case-Team-Leader-QC-Pending',
                              'Waiting-For-Closer': '/My-Case-Waiting-For-Closer',
                              'Closed': '/My-Case-Closed',
                            };

                            targetUrl = routeMapping[routeName] || `/My-Case-${routeName}`;
                          }
                        }

                        if (keepMotorSuffix) {
                          targetUrl = appendMotorRouteSuffix(stripMotorRouteSuffix(targetUrl));
                        }

                        setActiveTab(targetUrl);
                        navigate(targetUrl);
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      borderRadius: "10px",
                      border: "none",
                      boxShadow: isActive
                        ? "0 6px 20px rgba(16, 183, 89, 0.4), 0 3px 8px rgba(0, 0, 0, 0.1)"
                        : "0 2px 6px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      background: isActive
                        ? "linear-gradient(135deg, #10b759 0%, #059669 100%)"
                        : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                      transform: isActive ? "translateY(-2px) scale(1.02)" : "translateY(0) scale(1)",
                      position: "relative",
                      overflow: "visible",
                      height: "auto",
                      minHeight: "36px",
                      display: "flex",
                      alignItems: "center"
                    }}
                    className="tab-card"
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.transform = "translateY(-1px) scale(1.01)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.transform = "translateY(0) scale(1)";
                        e.currentTarget.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.08)";
                      }
                    }}
                  >
                    {/* Count badge */}
                    {(() => {
                      const labelKey = normalize(child.label);
                      const lookupKey = statusSynonyms[labelKey] || labelKey;
                      const count = statusCountMap[lookupKey] ?? 0;
                      return (
                        <div style={{
                          position: "absolute",
                          top: "-8px",
                          right: "-8px",
                          background: "#ff4757",
                          color: "#fff",
                          borderRadius: "999px",
                          minWidth: "18px",
                          height: "18px",
                          padding: "0 6px",
                          fontSize: "10px",
                          lineHeight: 1,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                          zIndex: 2,
                        }}>
                          {count}
                        </div>
                      );
                    })()}
                    {/* Active indicator */}
                    {isActive && (
                      <div style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        background: "linear-gradient(90deg, #10b759, #059669, #047857)",
                        borderRadius: "10px 10px 0 0"
                      }} />
                    )}

                    <Card.Body
                      style={{
                        padding: "8px 12px",
                        textAlign: "center",
                        position: "relative",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: isActive ? "700" : "600",
                          color: isActive ? "#ffffff" : "#495057",
                          lineHeight: 1.2,
                          textTransform: "capitalize",
                          letterSpacing: "0.2px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "100%",
                          textAlign: "center"
                        }}
                        title={child.label}
                      >
                        {child.label}
                      </div>

                      {/* Subtle icon for active state */}
                      {isActive && (
                        <div style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          width: 0,
                          height: 0,
                          borderRadius: "50%",
                          background: "rgba(255, 255, 255, 0.8)",
                          animation: "pulse 2s infinite"
                        }} />
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
        </Row>
      </div>

      <Row>
        <Col>
          <Card>
            <Card.Body>
              <div className="mb-2">
                <Row>
                  <Col lg={3}>
                  </Col>
                  <Col lg={9}>
                    <div className="d-flex justify-content-end align-items-center">
                      <div className="button-list">
                        {(Object.keys(filters).length > 0) && (
                          <>
                            <Button variant="warning" onClick={() => setFilters({})}>
                              <i className="mdi mdi-filter-off me-1"></i>Clear All Filter
                            </Button>
                          </>
                        )}
                        {(activeCaseDetailsPermissions?.add) && isInitiatedListEndpoint && (
                          <>
                            <Button variant="success" onClick={() => setShowCaseInitiatedModel(true)}>
                              <i className="mdi mdi-clipboard-plus-outline me-1"></i>Add
                            </Button>
                          </>
                        )}
                        {(activeCaseDetailsPermissions?.print) && (<>
                          <Button variant="dark">
                            <i className="mdi mdi-printer me-1"></i>Print
                          </Button>
                        </>)}
                        {(activeCaseDetailsPermissions?.excel) && (<>
                          <Button variant="info">
                            <i className="mdi mdi-file-excel me-1"></i>Excel
                          </Button>
                        </>)}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="table-responsive" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '500px', border: '1px solid #dee2e6' }}>
                <Table
                  className="mb-0 table-bordered"
                  size="sm"
                  striped
                  style={{ width: '150%', tableLayout: 'fixed' }}
                >
                  <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1 }}>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      <th style={{ width: '150px' }}>
                        Remaining TAT
                      </th>
                      <th style={{ width: '200px', whiteSpace: 'normal' }}>
                        TPA Name
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("tpa_name")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '200px', whiteSpace: 'normal' }}>
                        Insurance Company
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("insurance")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '160px', whiteSpace: 'normal' }}>
                        Patient Name
                        {caseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("patient_name")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '160px', whiteSpace: 'normal' }}>
                        Type Of Claim
                        {caseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("claim_type")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '200px', whiteSpace: 'normal' }}>
                        Case Manager
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("case_manager")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '160px', whiteSpace: 'normal' }}>
                        Claim Number
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("claim_number")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '160px', whiteSpace: 'normal' }}>
                        Policy Number
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("policy_number")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '200px', whiteSpace: 'normal' }}>
                        Hospital Name
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("hospital")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '160px', whiteSpace: 'normal' }}>
                        Hospital City
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("hospital_city")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '160px', whiteSpace: 'normal' }}>
                        Hospital State
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("hospital_state_name")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '190px', whiteSpace: 'normal' }}>
                        Insurance Manager
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("manager")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '200px', whiteSpace: 'normal' }}>
                        Field Officer Name
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("field_officer_name")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '350px', whiteSpace: 'normal' }}>
                        Trigger
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("first_trigger")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '170px', whiteSpace: 'normal' }}>
                        Case Open Date
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("case_open_date")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '170px', whiteSpace: 'normal' }}>
                        Case Open Time
                        {activeCaseDetailsPermissions?.filter && (<>
                          <Button variant="link" onClick={() => handleFilterClick("case_open_time")}><FontAwesomeIcon icon={faFilter} /></Button>
                        </>)}
                      </th>
                      <th style={{ width: '140px', whiteSpace: 'normal' }}>
                        Status
                      </th>
                      <th style={{ width: '160px', whiteSpace: 'normal' }}>Created By</th>
                      <th style={{ width: '160px', whiteSpace: 'normal' }}>Registered At<br /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeCaseDetailsLists.length === 0 ? (
                      <tr>
                        <td colSpan="16">No data found</td>
                      </tr>
                    ) : (
                      <>
                        {activeCaseDetailsLists?.map?.((item, index) => {
                          return (

                            <tr
                              key={item?.id}
                              onClick={() => navigate(getCaseSummaryPath(item?.id, location.pathname))}
                              style={{ cursor: "pointer" }}
                            >
                              <th scope="row" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {index + 1 + (page - 1) * paginate}
                              </th>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                <Badge bg={calculateTimeRemaining(item?.case_close_date, item?.claimTypeCaseDetails?.name) === 'Expired' ? 'danger' : 'warning'}>
                                  {calculateTimeRemaining(item?.case_close_date, item?.claimTypeCaseDetails?.name)}
                                </Badge>
                              </td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.tpaIdCaseDetails?.name}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.insuranceIdCaseDetails?.name}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.patient_name}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.claimTypeCaseDetails?.name}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.caseManagerIdCaseDetails?.name}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.claim_number}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.policy_number}</td>
                             
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.hospitaldCaseDetails?.hospital_name}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.hospitaldCaseDetails?.hospital_cities?.city_name}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.hospitaldCaseDetails?.hospital_cities?.cities_state?.state_name}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.ManagerIdCaseDetails?.name}</td> 
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {(() => {
                                  const allocations = item?.caseIdCaseFieldOfficerAllocation;
                                  const list = allocations?.length > 0
                                    ? allocations.map((a) => a?.foIdCaseFieldOfficerAllocation).filter(Boolean)
                                    : item?.foIdCaseFieldOfficerAllocation ? [item.foIdCaseFieldOfficerAllocation] : [];
                                  // unique by FO id (same FO multiple times assign ho sakta hai)
                                  const unique = list.filter((fo, i, arr) => arr.findIndex((f) => f?.id === fo?.id) === i);
                                  if (unique.length === 0) return '—';
                                  return (
                                    <>
                                      {unique.map((fo, i) => (
                                        <span key={fo?.id ?? i} className="d-block mb-1">
                                          {fo?.name && <span className="d-block fw-medium">{fo.name}</span>}
                                          {fo?.phone && <span className="d-block small text-muted">{fo.phone}</span>}
                                          {i < unique.length - 1 && <span className="d-block border-bottom my-1" style={{ borderColor: '#dee2e6' }} />}
                                        </span>
                                      ))}
                                    </>
                                  );
                                })()}
                              </td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.first_trigger}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.case_open_date}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.case_open_time}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.case_status}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item?.createdByCaseDetails?.name}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{getDisplayDateTimeFormat(item?.createdAt)}</td>
                            </tr>

                          );
                        })}
                      </>
                    )}
                  </tbody>
                </Table>
              </div>
              <div class="mt-3 d-lg-flex align-items-center text-center pb-1">
                <div class="d-inline-block me-3">
                  <label class="me-1">Display :</label>
                  <select class="form-select d-inline-block w-auto" onChange={handlePaginateChange}>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
                <span class="me-3">Page <strong>{page}</strong> of <strong>{Math.ceil(activeCaseDetailsCount / paginate)}</strong> </span>
                <RoundedPagination currentPage={page} totalPages={Math.ceil(activeCaseDetailsCount / paginate)} onPageChange={(pageNumber) => setPage(pageNumber)} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <AddCaseInitiated
        show={showCaseInitiatedModel}
        onHide={() => hideCaseInitiatedModel()}
        claimCategory={caseClaimCategory}
      />

      <FilterModal
        show={modal.show}
        onHide={() => setModal({ show: false, key: "", type: "", value: "" })}
        modalData={modal}
        onApplyFilter={handleApplyFilter}
        onRemoveFilter={handleRemoveFilter}
        onChange={handleModalChange}
        claimTypeOptions={masterClaimTypeList}
      />
      {activeLoadingCaseDetails && (<Preloader />)}
    </>
  );
};

export default CaseDetailsIndex;